import React, { useState, useEffect, useRef, useCallback } from 'react';
import './HaloSearch.css';
import { v4 as uuidv4 } from 'uuid';
import { cameras } from '../../data/cameras';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

// API base URL
const API_BASE_URL = 'http://localhost:8000';
const APP_LOGO_SRC = `${process.env.PUBLIC_URL}/logo192.png`;

// Mapbox token
const MAPBOX_TOKEN = 'pk.eyJ1IjoiZGFiMSIsImEiOiJjbDZmeGw3bm0wYTN1M2JtamV6cW1jZWVqIn0.e-0qo3T9hooEEc2rnoo9Ag';

// Set the token globally
mapboxgl.accessToken = MAPBOX_TOKEN;

interface HaloSearchProps {
  onSearch: (description: string, searchType: string) => void;
  onLocationSelect?: (lat: string, lon: string) => void;
}

// Add search type enum
type SearchType = 'person' | 'object';

interface RadiusSelection {
  center: [number, number] | null;
  radius: number | null;
  isSelected: boolean;
}

const HaloIcon = () => (
  <div className="halo-logo">
    <img src={APP_LOGO_SRC} alt="Archangel AI logo" width={24} height={24} className="halo-svg" />
    <span className="halo-text">Halo Search</span>
  </div>
);

const ExitIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
    <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const WarningIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="rgb(255, 59, 48)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 8V12" stroke="rgb(255, 59, 48)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 16H12.01" stroke="rgb(255, 59, 48)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ErrorIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 22C17.5228 22 22 17.5228 22 12C22 6.47715 17.5228 2 12 2C6.47715 2 2 6.47715 2 12C2 17.5228 6.47715 22 12 22Z" stroke="rgb(255, 59, 48)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M15 9L9 15" stroke="rgb(255, 59, 48)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 9L15 15" stroke="rgb(255, 59, 48)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const TargetIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="rgb(52, 199, 89)" strokeWidth="2" />
    <circle cx="12" cy="12" r="6" stroke="rgb(52, 199, 89)" strokeWidth="2" />
    <circle cx="12" cy="12" r="2" fill="rgb(52, 199, 89)" />
  </svg>
);

const SearchIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M11 19C15.4183 19 19 15.4183 19 11C19 6.58172 15.4183 3 11 3C6.58172 3 3 6.58172 3 11C3 15.4183 6.58172 19 11 19Z" stroke="rgb(0, 122, 255)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M21 21L16.65 16.65" stroke="rgb(0, 122, 255)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LoadingSpinner = () => (
  <div className="loading-spinner">
    <div className="spinner-container">
      <div className="spinner-ring"></div>
      <div className="spinner-ring"></div>
      <div className="spinner-ring"></div>
      <div className="spinner-dot"></div>
    </div>
    <div className="spinner-text">
      <span className="text-animate">Analyzing footage</span>
    </div>
  </div>
);

interface ServerMatch {
  imageUrl: string;
  cameraName: string;
  timestamp: string;
  explanation: string;
}

interface Match {
  imageId: string;
  timestamp: string;
  explanation: string;
  imageUrl?: string;
}

interface HaloResponse {
  matches: ServerMatch[];
  logs: string[];
}

interface SavedResult {
  timestamp: string;
  description: string;
  result: HaloResponse;
}

const formatErrorMessage = (log: string) => {
  if (log.includes('Error analyzing')) {
    if (log.includes('Status 429')) {
      return 'Rate limit reached. Please try again in a few minutes.';
    }
    if (log.includes('Status 404')) {
      return 'Image not found. Please check the file path.';
    }
    return 'Error analyzing image. Please try again.';
  }
  if (log.includes('Analyzing')) {
    return log; // Keep the "Analyzing X.jpg..." messages as is
  }
  return null; // Don't show other messages
};

const ResultSummary: React.FC<{
  logs: string[],
  matches: any[],
  isAnalyzing: boolean
}> = ({ logs, matches, isAnalyzing }) => {
  const hasRateLimit = logs.some(log => log.includes('Status 429'));
  const hasErrors = logs.some(log => log.includes('Error') && !log.includes('Status 429'));

  if (hasRateLimit) {
    return (
      <div className="halo-result-summary error">
        <div className="halo-result-icon">
          <WarningIcon />
        </div>
        <div className="halo-result-message">
          <h3>Analysis Interrupted</h3>
          <p>We've hit our rate limit. Please try again in a few minutes.</p>
        </div>
      </div>
    );
  }

  if (hasErrors) {
    return (
      <div className="halo-result-summary error">
        <div className="halo-result-icon">
          <ErrorIcon />
        </div>
        <div className="halo-result-message">
          <h3>Analysis Failed</h3>
          <p>There was an error analyzing some images. Please try again.</p>
        </div>
      </div>
    );
  }

  if (matches && matches.length > 0) {
    return (
      <div className="halo-result-summary success">
        <div className="halo-result-icon">
          <TargetIcon />
        </div>
        <div className="halo-result-message">
          <h3>Match Found!</h3>
          <p>Found {matches.length} potential match{matches.length > 1 ? 'es' : ''}.</p>
        </div>
      </div>
    );
  }

  if (logs.length > 0 && !isAnalyzing) {
    return (
      <div className="halo-result-summary info">
        <div className="halo-result-icon">
          <SearchIcon />
        </div>
        <div className="halo-result-message">
          <h3>No Matches Found</h3>
          <p>We analyzed all available footage but didn't find any matches for your description.</p>
        </div>
      </div>
    );
  }

  return null;
};

interface CameraDetails {
  location: string;
  lat: string;
  lon: string;
  iframeUrl?: string;
}

const MatchCard: React.FC<{
  match: Match,
  onLocationSelect?: (lat: string, lon: string) => void
}> = ({ match, onLocationSelect }) => {
  const [cameraDetails, setCameraDetails] = useState<CameraDetails | null>(null);
  const [showLiveFeed, setShowLiveFeed] = useState(false);
  const [imageError, setImageError] = useState(false);

  useEffect(() => {
    // Find camera details from cameras.ts
    const camera = cameras.find(c => c.camera_id === match.imageId);
    if (camera) {
      setCameraDetails({
        location: camera.location,
        lat: camera.lat,
        lon: camera.lon,
        iframeUrl: camera.iframeUrl
      });
    }
  }, [match.imageId]);

  const handleLocationClick = () => {
    if (cameraDetails && onLocationSelect) {
      onLocationSelect(cameraDetails.lat, cameraDetails.lon);
    }
  };

  const toggleLiveFeed = () => {
    setShowLiveFeed(!showLiveFeed);
  };

  // Function to get the correct image path
  const getImagePath = () => {
    if (match.imageUrl) {
      // Backend already returns a public path like /screenshots/32.jpg
      return `${API_BASE_URL}${match.imageUrl}`;
    }
    return `${API_BASE_URL}/screenshots/${match.imageId}.jpg`;
  };

  return (
    <div className="halo-match-card">
      <div className="halo-match-image-container">
        <img
          src={getImagePath()}
          alt={`Suspect match from camera ${match.imageId}`}
          className={`halo-match-image ${imageError ? 'image-error' : ''}`}
          onError={(e) => {
            console.error('Image failed to load:', getImagePath());
            setImageError(true);
            const target = e.target as HTMLImageElement;
            target.style.display = 'none';
          }}
        />
        {imageError && (
          <div className="halo-image-error">
            <ImageErrorIcon />
            <span>Image not available</span>
          </div>
        )}
        <div className="halo-match-overlay">
          {!imageError && (
            <button
              className="halo-match-zoom"
              onClick={() => window.open(getImagePath(), '_blank')}
            >
              <ZoomIcon />
            </button>
          )}
          {cameraDetails && (
            <button
              className="halo-location-button-overlay"
              onClick={handleLocationClick}
              title="Zoom to location on map"
            >
              <MapPinIcon />
            </button>
          )}
        </div>
      </div>

      <div className="halo-match-details">
        {cameraDetails && (
          <>
            <div className="halo-match-location">
              <LocationIcon />
              <span>{cameraDetails.location}</span>
              <button
                className="halo-location-button"
                onClick={handleLocationClick}
              >
                <MapPinIcon />
                Zoom to location
              </button>
            </div>

            <div className="halo-match-time">
              <ClockIcon />
              <span>{match.timestamp}</span>
            </div>

            {cameraDetails.iframeUrl && (
              <button
                className="halo-live-feed-button"
                onClick={toggleLiveFeed}
              >
                <LiveIcon />
                {showLiveFeed ? 'Hide Live Feed' : 'Show Live Feed'}
              </button>
            )}

            {showLiveFeed && cameraDetails.iframeUrl && (
              <div className="halo-live-feed">
                <iframe
                  src={cameraDetails.iframeUrl}
                  width="100%"
                  height="400"
                  frameBorder="0"
                  allowFullScreen
                  title="Live camera feed"
                />
              </div>
            )}
          </>
        )}

        <div className="halo-match-explanation">
          {match.explanation}
        </div>
      </div>
    </div>
  );
};

// Add new icon components
const ZoomIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M15 3H21V9" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M9 21H3V15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M21 3L14 10" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M3 21L10 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LocationIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 2C7.58172 2 4 5.58172 4 10C4 11.8489 4.63214 13.5517 5.69754 14.9291L12 22L18.3025 14.9291C19.3679 13.5517 20 11.8489 20 10C20 5.58172 16.4183 2 12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const MapPinIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M20 10C20 16 12 22 12 22C12 22 4 16 4 10C4 5.58172 7.58172 2 12 2C16.4183 2 20 5.58172 20 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 13C13.6569 13 15 11.6569 15 10C15 8.34315 13.6569 7 12 7C10.3431 7 9 8.34315 9 10C9 11.6569 10.3431 13 12 13Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ClockIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" />
    <path d="M12 6V12L16 14" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
  </svg>
);

const LiveIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M23 7L16 12L23 17V7Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 5H3C1.89543 5 1 5.89543 1 7V17C1 18.1046 1.89543 19 3 19H14C15.1046 19 16 18.1046 16 17V7C16 5.89543 15.1046 5 14 5Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

// Add new ImageError icon
const ImageErrorIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 3H5C3.89543 3 3 3.89543 3 5V19C3 20.1046 3.89543 21 5 21H19C20.1046 21 21 20.1046 21 19V5C21 3.89543 20.1046 3 19 3Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M8.5 10C9.32843 10 10 9.32843 10 8.5C10 7.67157 9.32843 7 8.5 7C7.67157 7 7 7.67157 7 8.5C7 9.32843 7.67157 10 8.5 10Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M21 15L16 10L5 21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const HaloSearch: React.FC<HaloSearchProps> = ({ onSearch, onLocationSelect }) => {
  const [description, setDescription] = useState('');
  const [searchType, setSearchType] = useState<SearchType>('person');
  const [isExpanded, setIsExpanded] = useState(false);
  const [response, setResponse] = useState<{ matches: Match[], logs: string[] } | null>(null);
  const [savedResults, setSavedResults] = useState<SavedResult[]>([]);
  const [showingPrevious, setShowingPrevious] = useState(false);
  const [isLoadingPrevious, setIsLoadingPrevious] = useState(false);
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [currentLogs, setCurrentLogs] = useState<string[]>([]);
  const [showResults, setShowResults] = useState(false);
  const [radiusSelection, setRadiusSelection] = useState<RadiusSelection>({
    center: null,
    radius: null,
    isSelected: false
  });
  const searchRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const iconRef = useRef<HTMLDivElement>(null);
  const exitRef = useRef<HTMLButtonElement>(null);
  const logsEndRef = useRef<HTMLDivElement>(null);
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<mapboxgl.Map | null>(null);
  const radiusCircleRef = useRef<mapboxgl.CircleLayer | null>(null);
  const eventSourceRef = useRef<EventSource | null>(null);
  const clientIdRef = useRef<string>(uuidv4());
  const [showBetaNotice, setShowBetaNotice] = useState(false);
  const betaNoticeTimeoutRef = useRef<number | null>(null);

  // Initialize map when component mounts
  useEffect(() => {
    if (isExpanded && mapContainerRef.current && !mapRef.current) {
      const map = new mapboxgl.Map({
        container: mapContainerRef.current,
        style: 'mapbox://styles/mapbox/dark-v11',
        center: [-122.4194, 37.7749], // San Francisco
        zoom: 12
      });

      map.on('load', () => {
        // Add camera markers
        cameras.forEach(camera => {
          const el = document.createElement('div');
          el.className = 'camera-marker';
          el.style.width = '12px';
          el.style.height = '12px';
          el.style.borderRadius = '50%';
          el.style.backgroundColor = '#FFD700';
          el.style.border = '2px solid #fff';
          el.style.cursor = 'pointer';

          new mapboxgl.Marker(el)
            .setLngLat([parseFloat(camera.lon), parseFloat(camera.lat)])
            .addTo(map);
        });

        // Add click handler for radius selection
        map.on('click', (e) => {
          const { lng, lat } = e.lngLat;
          setRadiusSelection({
            center: [lng, lat],
            radius: 25, // Default 25 meters
            isSelected: true
          });
        });

        mapRef.current = map;
      });
    }
  }, [isExpanded]);



  // Helper function to add radius circle
  const addRadiusCircle = () => {
    if (mapRef.current && radiusSelection.center && radiusSelection.radius) {
      // Remove existing circle if it exists
      if (mapRef.current.getLayer('radius-circle')) {
        mapRef.current.removeLayer('radius-circle');
      }
      if (mapRef.current.getSource('radius-circle')) {
        mapRef.current.removeSource('radius-circle');
      }

      // Add new circle
      mapRef.current.addSource('radius-circle', {
        type: 'geojson',
        data: {
          type: 'Feature',
          geometry: {
            type: 'Point',
            coordinates: radiusSelection.center
          },
          properties: {}
        }
      });

      mapRef.current.addLayer({
        id: 'radius-circle',
        type: 'circle',
        source: 'radius-circle',
        paint: {
          'circle-radius': radiusSelection.radius,
          'circle-color': '#FFD700',
          'circle-opacity': 0.3,
          'circle-stroke-color': '#FFD700',
          'circle-stroke-width': 2
        }
      });
    }
  };

  // Update radius circle when selection changes
  useEffect(() => {
    addRadiusCircle();
  }, [radiusSelection]);

  // Helper function to check if camera is within radius
  const isCameraInRadius = (camera: any) => {
    if (!radiusSelection.center || !radiusSelection.radius) return false;

    const cameraLat = parseFloat(camera.lat);
    const cameraLon = parseFloat(camera.lon);
    const [centerLon, centerLat] = radiusSelection.center;

    // Calculate distance using Haversine formula
    const R = 6371000; // Earth's radius in meters
    const dLat = (cameraLat - centerLat) * Math.PI / 180;
    const dLon = (cameraLon - centerLon) * Math.PI / 180;
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) +
      Math.cos(centerLat * Math.PI / 180) * Math.cos(cameraLat * Math.PI / 180) *
      Math.sin(dLon / 2) * Math.sin(dLon / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const distance = R * c;

    return distance <= radiusSelection.radius;
  };

  const fetchSavedResults = async () => {
    setIsLoadingPrevious(true);
    try {
      const response = await fetch(`${API_BASE_URL}/api/halo/results`);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      // Sort by timestamp descending (most recent first)
      const sortedData = data.sort((a: SavedResult, b: SavedResult) =>
        new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
      );
      setSavedResults(sortedData);
      setShowingPrevious(true);
    } catch (error) {
      console.error('Error fetching saved results:', error);
    } finally {
      setIsLoadingPrevious(false);
    }
  };

  const scrollToBottom = () => {
    logsEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [currentLogs]);

  const cleanupEventSource = useCallback(() => {
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  useEffect(() => {
    return () => {
      cleanupEventSource();
      if (betaNoticeTimeoutRef.current) {
        window.clearTimeout(betaNoticeTimeoutRef.current);
        betaNoticeTimeoutRef.current = null;
      }
    };
  }, [cleanupEventSource]);

  const showBeta = useCallback(() => {
    setShowBetaNotice(true);
    if (betaNoticeTimeoutRef.current) {
      window.clearTimeout(betaNoticeTimeoutRef.current);
    }
    betaNoticeTimeoutRef.current = window.setTimeout(() => {
      setShowBetaNotice(false);
      betaNoticeTimeoutRef.current = null;
    }, 5000);
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Submit button clicked'); // Debug log

    if (!description.trim()) {
      console.log('No description provided'); // Debug log
      return;
    }

    console.log('Starting search with:', { description, searchType, radiusSelection }); // Debug log

    setResponse(null);
    setShowingPrevious(false);
    setIsAnalyzing(true);
    setCurrentLogs([]);

    try {
      // Filter cameras by radius if radius is selected
      let camerasInRadius = cameras;
      if (radiusSelection.isSelected && radiusSelection.center && radiusSelection.radius) {
        camerasInRadius = cameras.filter(isCameraInRadius);
        console.log(`Filtered to ${camerasInRadius.length} cameras in radius`); // Debug log
      } else {
        console.log(`Searching all ${camerasInRadius.length} cameras (no radius selected)`); // Debug log
      }

      const requestBody = {
        description,
        region: 'All Regions',
        searchType,
        ...(radiusSelection.isSelected && radiusSelection.center && radiusSelection.radius && {
          radius: {
            center: radiusSelection.center,
            radius: radiusSelection.radius
          }
        })
      };

      console.log('Sending request:', requestBody); // Debug log

      const response = await fetch('http://localhost:8000/api/halo/run', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestBody),
      });

      console.log('Response status:', response.status); // Debug log

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data: HaloResponse = await response.json();
      console.log('Response data:', data); // Debug log

      // Transform server matches to our Match interface
      const transformedMatches: Match[] = data.matches.map(serverMatch => {
        // Extract camera ID from the analyzing logs
        const imageIdMatch = currentLogs
          .find(log => log.includes('Analyzing'))
          ?.match(/Analyzing (\d+)\.jpg/);

        // If we can't find the ID in logs, try to extract from cameraName or default to '41'
        const imageId = imageIdMatch ?
          imageIdMatch[1] :
          (serverMatch.cameraName?.match(/\d+/)?.[0] || '41');

        return {
          imageId,
          timestamp: serverMatch.timestamp || new Date().toLocaleTimeString(),
          explanation: serverMatch.explanation || 'Match found in surveillance footage',
          imageUrl: serverMatch.imageUrl
        };
      });

      console.log('Transformed matches:', transformedMatches); // Debug log

      setResponse({
        matches: transformedMatches,
        logs: data.logs || []
      });

      setCurrentLogs(data.logs || []);
      setShowResults(true); // Show results prominently

      if (transformedMatches.length > 0) {
        onSearch(description, searchType);

        // If we have a match, try to zoom to its location
        const firstMatch = transformedMatches[0];
        const camera = cameras.find(c => c.camera_id === firstMatch.imageId);
        if (camera && onLocationSelect) {
          onLocationSelect(camera.lat, camera.lon);
        }
      }
    } catch (error) {
      console.error('Error:', error);
      setCurrentLogs(['Error occurred during analysis']);
    } finally {
      setIsAnalyzing(false);
    }
  };

  const handleClose = () => {
    setIsExpanded(false);
    setDescription('');
    setResponse(null);
    setShowingPrevious(false);
    setCurrentLogs([]);
    setShowResults(false);
    // Return focus to the icon
    setTimeout(() => {
      iconRef.current?.focus();
    }, 100);
  };

  const handleExpandClick = () => {
    setIsExpanded(true);
    // Focus the input after expansion
    setTimeout(() => {
      inputRef.current?.focus();
    }, 100);
  };

  // Handle keyboard events
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      // Open on CMD/CTRL + K
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();
        // Block access in beta and show notice instead of expanding
        showBeta();
      }
      // Close on escape
      if (event.key === 'Escape' && isExpanded) {
        event.preventDefault();
        handleClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isExpanded]);

  // Handle clicks outside to close
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        isExpanded &&
        searchRef.current &&
        !searchRef.current.contains(event.target as Node) &&
        event.target !== iconRef.current
      ) {
        handleClose();
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isExpanded]);

  return (
    <>
      {/* Fullscreen Loader */}
      {isAnalyzing && (
        <div className="halo-fullscreen-loader">
          <LoadingSpinner />
          <div className="halo-fullscreen-loader-text">
            {/* <h2>Analyzing Surveillance Footage</h2>
            <p>Please wait while we search through camera feeds...</p> */}
          </div>
        </div>
      )}

      <div ref={searchRef} className={`halo-search ${isExpanded ? 'expanded' : ''}`}>
        <div
          ref={iconRef}
          className="halo-icon"
          onClick={(e) => {
            e.preventDefault();
            showBeta();
          }}
          tabIndex={0}
          role="button"
          aria-label="Halo Search (beta restricted)"
          aria-disabled="true"
          title="Halo Search is not available in the beta"
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              showBeta();
            }
          }}
        >
          <HaloIcon />
        </div>
        {showBetaNotice && (
          <div className="halo-beta-notice" role="status">
            <strong>Halo Search</strong>
            <p>Not part of the beta. Access is limited to law enforcement.</p>
            <button
              className="halo-beta-dismiss"
              aria-label="Dismiss notice"
              onClick={() => setShowBetaNotice(false)}
            >
              ×
            </button>
          </div>
        )}
        <div className={`halo-expanded-content ${isExpanded ? 'show' : ''}`}>
          <button
            ref={exitRef}
            className="halo-exit-button"
            onClick={handleClose}
            aria-label="Close Halo search"
          >
            <ExitIcon />
          </button>
          {/* Search Form and Map Container */}
          <div className="halo-search-and-map-container">
            <form onSubmit={handleSubmit} className="halo-search-form">
              {/* Add search type toggle */}
              <div className="halo-search-type-toggle">
                <label className="halo-toggle-label">Search Type:</label>
                <div className="halo-toggle-container">
                  <button
                    type="button"
                    className={`halo-toggle-option ${searchType === 'person' ? 'active' : ''}`}
                    onClick={() => setSearchType('person')}
                  >
                    {searchType === 'person' ? <SelectedHaloIcon /> : <PersonIcon />}
                    Person
                  </button>
                  <button
                    type="button"
                    className={`halo-toggle-option ${searchType === 'object' ? 'active' : ''}`}
                    onClick={() => setSearchType('object')}
                  >
                    {searchType === 'object' ? <SelectedHaloIcon /> : <ObjectIcon />}
                    Object
                  </button>
                </div>
              </div>

              <input
                ref={inputRef}
                type="text"
                placeholder={
                  searchType === 'person'
                    ? "Enter suspect description (e.g., 'Male wearing red hoodie, black shoes, carrying backpack')"
                    : "Enter object description (e.g., 'Red car, black backpack, blue bicycle')"
                }
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                className="halo-input"
              />
              <div className="halo-button-group">
                <button
                  type="submit"
                  className="halo-submit"
                  disabled={!description.trim() || isAnalyzing || !radiusSelection.isSelected}
                  onClick={() => console.log('Button clicked!')}
                >
                  {isAnalyzing ? 'Analyzing...' : 'Run Halo'}
                </button>
                <button
                  type="button"
                  className="halo-previous"
                  onClick={() => showingPrevious ? setShowingPrevious(false) : fetchSavedResults()}
                  disabled={isLoadingPrevious}
                >
                  {isLoadingPrevious ? 'Loading...' : showingPrevious ? 'Hide Previous' : 'Show Previous'}
                </button>
              </div>
              {!radiusSelection.isSelected && description.trim() && (
                <div className="radius-required-message">
                  <p>⚠️ Please select a radius on the map to filter your search area</p>
                </div>
              )}
            </form>

            {/* Radius Selection Map - side by side with search form */}
            <div className="halo-radius-section-sidebar">
              <div className="halo-radius-header">
                <h3>Search Area</h3>
                <p>Click to set search radius</p>
                {radiusSelection.isSelected && (
                  <div className="radius-info">
                    <span>Selected: {radiusSelection.radius}m radius</span>
                    <button
                      className="clear-radius-btn"
                      onClick={() => {
                        // Remove circle from map if it exists
                        if (mapRef.current) {
                          if (mapRef.current.getLayer('radius-circle')) {
                            mapRef.current.removeLayer('radius-circle');
                          }
                          if (mapRef.current.getSource('radius-circle')) {
                            mapRef.current.removeSource('radius-circle');
                          }
                        }
                        setRadiusSelection({ center: null, radius: null, isSelected: false });
                      }}
                    >
                      Clear
                    </button>
                  </div>
                )}
              </div>
              <div className="halo-map-container">
                <div ref={mapContainerRef} className="halo-map" />
              </div>
              <div className="radius-controls">
                <label>Radius (meters):</label>
                <input
                  type="range"
                  min="25"
                  max="1000"
                  step="25"
                  value={radiusSelection.radius || 25}
                  onChange={(e) => {
                    if (radiusSelection.center) {
                      setRadiusSelection({
                        ...radiusSelection,
                        radius: parseInt(e.target.value)
                      });
                    }
                  }}
                  disabled={!radiusSelection.isSelected}
                />
                <span>{radiusSelection.radius || 25}m</span>
              </div>
            </div>
          </div>

          <br></br>
          <br></br>

          <br></br>


          {/* Results Section - full width below search */}
          {showResults && response && (
            <div className="halo-results-full-width">


              {/* Show result summary */}
              <ResultSummary
                logs={currentLogs}
                matches={response.matches}
                isAnalyzing={isAnalyzing}
              />

              {/* Show matches if any */}
              {response.matches && response.matches.length > 0 && (
                <div className="halo-matches">
                  <div className="halo-matches-grid-full">
                    {response.matches.map((match, index) => (
                      <MatchCard
                        key={index}
                        match={match}
                        onLocationSelect={onLocationSelect}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}



          {/* Original logs section - only show when not showing results prominently */}
          {!showResults && (
            <div className="halo-logs">
              {!showingPrevious && (
                <>
                  {/* Show result summary */}
                  {response && (
                    <ResultSummary
                      logs={currentLogs}
                      matches={response.matches}
                      isAnalyzing={isAnalyzing}
                    />
                  )}

                  {/* Show matches if any */}
                  {response?.matches && response.matches.length > 0 && (
                    <div className="halo-matches">
                      <div className="halo-matches-header">
                        <h3>🔍 Search Results</h3>
                        <p>Found {response.matches.length} potential match{response.matches.length > 1 ? 'es' : ''}</p>
                      </div>
                      {response.matches.map((match, index) => (
                        <MatchCard
                          key={index}
                          match={match}
                          onLocationSelect={onLocationSelect}
                        />
                      ))}
                    </div>
                  )}


                </>
              )}
              {showingPrevious && savedResults.length > 0 && (
                <>
                  <div className="halo-log-line">
                    <strong>Previous Results:</strong>
                  </div>
                  {savedResults.map((result, index) => (
                    <div key={index} className="halo-saved-result">
                      <div className="halo-saved-result-header">
                        <span className="timestamp">{result.timestamp}</span>
                        <span className="description">{result.description}</span>
                      </div>
                      <pre>{JSON.stringify(result.result, null, 2)}</pre>
                    </div>
                  ))}
                </>
              )}
              <div ref={logsEndRef} />
            </div>
          )}
        </div>
      </div>
    </>
  );
};

// Add new icon components for the toggle with floating animations
const SelectedHaloIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="selected-halo-svg">
    <path
      className="selected-halo-outer-ring"
      d="M4 12C4 7.58172 7.58172 4 12 4C16.4183 4 20 7.58172 20 12"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      className="selected-halo-inner-ring"
      d="M6 12C6 8.68629 8.68629 6 12 6C15.3137 6 18 8.68629 18 12"
      strokeWidth="1.5"
      opacity="0.8"
    />
  </svg>
);

const PersonIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="person-svg">
    <path
      className="person-body"
      d="M20 21V19C20 17.9391 19.5786 16.9217 18.8284 16.1716C18.0783 15.4214 17.0609 15 16 15H8C6.93913 15 5.92172 15.4214 5.17157 16.1716C4.42143 16.9217 4 17.9391 4 19V21"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <circle
      className="person-head"
      cx="12"
      cy="7"
      r="4"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

const ObjectIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" className="object-svg">
    <path
      className="object-body"
      d="M21 16V8C21 6.89543 20.1046 6 19 6H5C3.89543 6 3 6.89543 3 8V16C3 17.1046 3.89543 18 5 18H19C20.1046 18 21 17.1046 21 16Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      className="object-line-1"
      d="M7 10H17"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
    <path
      className="object-line-2"
      d="M7 14H13"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
    />
  </svg>
);

export default HaloSearch; 